/**
 * BridgeMonitoring — Real-time Bridge + Cloudflare Tunnel Dashboard
 * 
 * Features:
 * - Auto-refresh every 3s
 * - Skeleton loading states
 * - Color-coded status indicators
 * - SVG timeline chart (no external deps)
 * - Log viewer with level filters
 * - Status code breakdown
 * - Toast notifications on status changes
 */

import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Spinner, Button } from '../components/ui';

// ═══ Design Tokens ═══
const theme = {
  bg: T.bg,
  card: T.bg,
  border: T.borderSubtle,
  text: T.text,
  dimmed: T.textMuted,
  accent: T.accent,
  success: T.success,
  error: T.danger,
  warning: T.warning,
  info: T.primary,
  input: T.bgInput,
};

// ═══ Types ═══

interface BridgeStatus {
  tunnel_status: 'connected' | 'disconnected' | 'error';
  tunnel_ip: string;
  tunnel_port: number;
  cloudflare_edges: number;
  process_status: 'running' | 'stopped';
  uptime_seconds: number;
  last_heartbeat: string;
  health_check: { http_ok: boolean; latency_ms: number; timestamp: string };
}

interface BridgeLog {
  id: string;
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  message: string;
  source: string;
}

interface BridgeStats {
  requests_per_minute: number;
  requests_total: number;
  bandwidth_mb: number;
  avg_latency_ms: number;
  error_rate: number;
  status_codes: Record<string, number>;
  period: string;
}

interface BridgeUptime {
  uptime_24h: number;
  downtime_minutes: number;
  events: Array<{ timestamp: string; status: string; duration_seconds: number }>;
}

interface TimelinePoint {
  timestamp: string;
  requests: number;
  errors: number;
  latency_ms: number;
}

// ═══ Helpers ═══

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
}

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
}

function timeAgo(iso: string): string {
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 5) return 'agora';
  if (diff < 60) return `${diff}s atrás`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
  return `${Math.floor(diff / 3600)}h atrás`;
}

function statusColor(s: string): string {
  if (s === 'connected' || s === 'running' || s === 'ok') return theme.success;
  if (s === 'error' || s === 'failed') return theme.error;
  return theme.warning;
}

function statusEmoji(s: string): string {
 if (s === 'connected' || s === 'running') return '';
 if (s === 'error' || s === 'failed') return '';
 return '';
}

function levelIcon(l: string): string {
 if (l === 'error') return '';
 if (l === 'warn') return '';
 return '';
}

// ═══ Skeleton ═══

function Skeleton({ w = '100%', h = 16 }: { w?: string | number; h?: number }) {
  return (
    <div style={{
      width: typeof w === 'number' ? `${w}px` : w,
      height: `${h}px`,
      background: `linear-gradient(90deg, ${theme.border} 25%, #2a2a3e 50%, ${theme.border} 75%)`,
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite',
      borderRadius: 4,
    }} />
  );
}

// ═══ Toast ═══

function Toast({ msg, type, onClose }: { msg: string; type: 'success' | 'error' | 'warn'; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, []);

  const bg = type === 'error' ? theme.error : type === 'warn' ? theme.warning : theme.success;
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 9999,
      background: bg, color: T.text, padding: '12px 20px',
      borderRadius: 8, fontSize: 14, fontWeight: 600,
      boxShadow: `0 4px 20px ${bg}40`,
      animation: 'fadeIn 0.3s',
    }}>
      {msg}
      <span onClick={onClose} style={{ marginLeft: 12, cursor: 'pointer', opacity: 0.7 }}></span>
    </div>
  );
}

// ═══ SVG Mini Chart ═══

function TimelineChart({ points }: { points: TimelinePoint[] }) {
  if (!points.length) return <div style={{ color: theme.dimmed, padding: 20, textAlign: 'center' }}>Sem dados</div>;

  const W = 700, H = 180, PAD = 40;
  const data = [...points].reverse(); // chronological order
  const maxReq = Math.max(...data.map(p => p.requests), 1);
  const maxLat = Math.max(...data.map(p => p.latency_ms), 1);

  const xScale = (i: number) => PAD + (i / Math.max(data.length - 1, 1)) * (W - PAD * 2);
  const yReq = (v: number) => H - PAD - (v / maxReq) * (H - PAD * 2);
  const yLat = (v: number) => H - PAD - (v / maxLat) * (H - PAD * 2);

  const reqPath = data.map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yReq(p.requests)}`).join(' ');
  const latPath = data.map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yLat(p.latency_ms)}`).join(' ');

  // Area fill for requests
  const reqArea = reqPath + ` L${xScale(data.length - 1)},${H - PAD} L${xScale(0)},${H - PAD} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxHeight: 200 }}>
      <defs>
        <linearGradient id="reqGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={theme.accent} stopOpacity="0.3" />
          <stop offset="100%" stopColor={theme.accent} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map(f => {
        const y = PAD + f * (H - PAD * 2);
        return <line key={f} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke={theme.border} strokeWidth="1" />;
      })}

      {/* Area fill */}
      <path d={reqArea} fill="url(#reqGrad)" />

      {/* Requests line */}
      <path d={reqPath} fill="none" stroke={theme.accent} strokeWidth="2" />

      {/* Latency line */}
      <path d={latPath} fill="none" stroke={theme.warning} strokeWidth="1.5" strokeDasharray="4,3" />

      {/* Data points */}
      {data.map((p, i) => (
        <circle key={i} cx={xScale(i)} cy={yReq(p.requests)} r="3" fill={p.errors > 0 ? theme.error : theme.accent}>
          <title>{fmtTime(p.timestamp)}: {p.requests} req, {p.errors} err, {p.latency_ms}ms</title>
        </circle>
      ))}

      {/* Y-axis labels */}
      <text x={PAD - 5} y={PAD + 4} fill={theme.accent} fontSize="10" textAnchor="end">{maxReq}</text>
      <text x={PAD - 5} y={H - PAD + 4} fill={theme.accent} fontSize="10" textAnchor="end">0</text>
      <text x={W - PAD + 5} y={PAD + 4} fill={theme.warning} fontSize="10">{maxLat}ms</text>
      <text x={W - PAD + 5} y={H - PAD + 4} fill={theme.warning} fontSize="10">0ms</text>

      {/* X-axis labels */}
      {data.filter((_, i) => i % Math.max(Math.floor(data.length / 5), 1) === 0).map((p, _, arr) => {
        const idx = data.indexOf(p);
        return (
          <text key={idx} x={xScale(idx)} y={H - 8} fill={theme.dimmed} fontSize="9" textAnchor="middle">
            {fmtTime(p.timestamp).slice(0, 5)}
          </text>
        );
      })}

      {/* Legend */}
      <rect x={W / 2 - 80} y={4} width={10} height={10} fill={theme.accent} rx="2" />
      <text x={W / 2 - 66} y={13} fill={theme.text} fontSize="10">Requisições</text>
      <rect x={W / 2 + 10} y={4} width={10} height={10} fill={theme.warning} rx="2" />
      <text x={W / 2 + 24} y={13} fill={theme.text} fontSize="10">Latência</text>
    </svg>
  );
}

// ═══ Status Codes Visual ═══

function StatusCodesBar({ codes }: { codes: Record<string, number> }) {
  const entries = Object.entries(codes).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;

  const codeColor = (c: string) => {
    if (c.startsWith('2')) return theme.success;
    if (c.startsWith('3')) return theme.info;
    if (c.startsWith('4')) return theme.warning;
    if (c.startsWith('5')) return theme.error;
    return theme.dimmed;
  };

  if (!entries.length) return <div style={{ color: theme.dimmed, padding: 12 }}>Sem dados de status codes</div>;

  return (
    <div>
      {/* Bar */}
      <div style={{ display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
        {entries.map(([code, cnt]) => (
          <div key={code} style={{
            width: `${(cnt / total) * 100}%`,
            background: codeColor(code),
            minWidth: cnt > 0 ? 4 : 0,
            transition: 'width 0.3s',
          }} title={`${code}: ${cnt} (${((cnt / total) * 100).toFixed(1)}%)`} />
        ))}
      </div>
      {/* Labels */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {entries.map(([code, cnt]) => (
          <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: codeColor(code), display: 'inline-block',
            }} />
            <span style={{ color: theme.text, fontWeight: 600 }}>{code}</span>
            <span style={{ color: theme.dimmed }}>{cnt} ({((cnt / total) * 100).toFixed(1)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══ Card Component ═══

function Card({ title, icon, children, style = {} }: {
  title: string; icon: string; children: any; style?: any;
}) {
  return (
    <div style={{
      background: theme.card, border: `1px solid ${theme.border}`,
      borderRadius: 12, padding: 20, ...style,
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: theme.dimmed, marginBottom: 14, letterSpacing: 0.5 }}>
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

// ═══ Stat Item ═══

function Stat({ label, value, color, sub }: { label: string; value: string | number; color?: string; sub?: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: theme.dimmed, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || theme.text }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: theme.dimmed }}>{sub}</div>}
    </div>
  );
}

// ═══ Main Component ═══

export function BridgeMonitoringTab() {
  const [status, setStatus] = useState<BridgeStatus | null>(null);
  const [logs, setLogs] = useState<BridgeLog[]>([]);
  const [stats, setStats] = useState<BridgeStats | null>(null);
  const [uptime, setUptime] = useState<BridgeUptime | null>(null);
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [logFilter, setLogFilter] = useState<'all' | 'info' | 'warn' | 'error'>('all');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'warn' } | null>(null);
  const prevStatus = useRef<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [sRes, lRes, stRes, uRes, tRes] = await Promise.allSettled([
        api.get('/api/v1/admin/bridge/monitoring/status'),
        api.get('/api/v1/admin/bridge/monitoring/logs?limit=100&hours=1'),
        api.get('/api/v1/admin/bridge/monitoring/stats'),
        api.get('/api/v1/admin/bridge/monitoring/uptime'),
        api.get('/api/v1/admin/bridge/monitoring/timeline'),
      ]);

      if (sRes.status === 'fulfilled' && sRes.value?.data) {
        const s = sRes.value.data as BridgeStatus;
        // Toast on status change
        if (prevStatus.current && prevStatus.current !== s.tunnel_status) {
          if (s.tunnel_status === 'disconnected' || s.tunnel_status === 'error') {
 setToast({ msg: `Bridge ${s.tunnel_status.toUpperCase()}!`, type: 'error' });
          } else if (s.tunnel_status === 'connected') {
 setToast({ msg: 'Bridge reconectada!', type: 'success' });
          }
        }
        prevStatus.current = s.tunnel_status;
        setStatus(s);
      }
      if (lRes.status === 'fulfilled' && lRes.value?.data) setLogs((lRes.value.data as any).logs || []);
      if (stRes.status === 'fulfilled' && stRes.value?.data) setStats(stRes.value.data as BridgeStats);
      if (uRes.status === 'fulfilled' && uRes.value?.data) setUptime(uRes.value.data as BridgeUptime);
      if (tRes.status === 'fulfilled' && tRes.value?.data) setTimeline((tRes.value.data as any).points || []);
    } catch (e) {
      console.error('Bridge monitoring fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 3000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const filteredLogs = logFilter === 'all' ? logs : logs.filter(l => l.level === logFilter);

 const headerStatus = status?.tunnel_status === 'connected' ? 'Online'
 : status?.tunnel_status === 'error' ? 'Error' : 'Offline';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <style>{`
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, padding: '16px 0', borderBottom: `1px solid ${theme.border}`,
      }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: theme.text }}>
 Bridge Monitoring
        </div>
        <div style={{
          fontSize: 14, fontWeight: 600,
          color: status?.tunnel_status === 'connected' ? theme.success : theme.error,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: status?.tunnel_status === 'connected' ? theme.success : theme.error,
            animation: status?.tunnel_status === 'connected' ? undefined : 'pulse 2s infinite',
          }} />
          {loading ? <><Spinner size="sm" inline /> Carregando...</> : headerStatus}
        </div>
      </div>

      {/* Row 1: Quick Status + Health */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
 <Card title="Status Rápido" icon="">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Skeleton h={20} /><Skeleton h={20} /><Skeleton h={20} /><Skeleton h={20} />
            </div>
          ) : (
            <>
              <Stat
                label="Tunnel"
                value={status?.tunnel_status || 'unknown'}
                color={statusColor(status?.tunnel_status || '')}
              />
              <Stat
                label="Uptime (24h)"
                value={uptime ? `${uptime.uptime_24h}%` : '—'}
                color={uptime && uptime.uptime_24h >= 99 ? theme.success : theme.warning}
                sub={uptime ? `${uptime.downtime_minutes}min down` : undefined}
              />
              <Stat label="Req/min" value={stats?.requests_per_minute || 0} />
              <Stat
                label="Taxa de Erros"
                value={`${stats?.error_rate || 0}%`}
                color={stats && stats.error_rate > 5 ? theme.error : stats && stats.error_rate > 1 ? theme.warning : theme.success}
              />
            </>
          )}
        </Card>

 <Card title="Health Check" icon="">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Skeleton h={20} /><Skeleton h={20} /><Skeleton h={20} /><Skeleton h={20} />
            </div>
          ) : (
            <>
              <Stat
                label="Status"
                value={status?.health_check.http_ok ? '200 OK' : 'FAILED'}
                color={status?.health_check.http_ok ? theme.success : theme.error}
              />
              <Stat
                label="Latência"
                value={`${status?.health_check.latency_ms || 0}ms`}
                color={status && status.health_check.latency_ms > 500 ? theme.error :
                  status && status.health_check.latency_ms > 200 ? theme.warning : theme.success}
              />
              <Stat label="Edges" value={`${status?.cloudflare_edges || 0} connected`} />
              <Stat
                label="Último check"
                value={status?.health_check.timestamp ? timeAgo(status.health_check.timestamp) : '—'}
              />
            </>
          )}
        </Card>
      </div>

      {/* Row 2: Timeline */}
 <Card title="Timeline (30 min)" icon="" style={{ marginBottom: 16 }}>
        {loading ? <Skeleton h={180} /> : <TimelineChart points={timeline} />}
      </Card>

      {/* Row 3: Status Codes */}
 <Card title="Status Codes" icon="" style={{ marginBottom: 16 }}>
        {loading ? <Skeleton h={60} /> : <StatusCodesBar codes={stats?.status_codes || {}} />}
      </Card>

      {/* Row 4: Bandwidth & Totals */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
 <Card title="Total Requisições" icon="">
          <div style={{ fontSize: 28, fontWeight: 700, color: theme.accent }}>
            {loading ? <Skeleton h={32} w={80} /> : (stats?.requests_total || 0).toLocaleString('pt-BR')}
          </div>
          <div style={{ fontSize: 12, color: theme.dimmed }}>{stats?.period || 'última 1h'}</div>
        </Card>
 <Card title="Bandwidth" icon="">
          <div style={{ fontSize: 28, fontWeight: 700, color: theme.info }}>
            {loading ? <Skeleton h={32} w={80} /> : `${stats?.bandwidth_mb || 0} MB`}
          </div>
          <div style={{ fontSize: 12, color: theme.dimmed }}>{stats?.period || 'última 1h'}</div>
        </Card>
 <Card title="Latência Média" icon="">
          <div style={{ fontSize: 28, fontWeight: 700, color: theme.warning }}>
            {loading ? <Skeleton h={32} w={80} /> : `${stats?.avg_latency_ms || 0}ms`}
          </div>
          <div style={{ fontSize: 12, color: theme.dimmed }}>{stats?.period || 'última 1h'}</div>
        </Card>
      </div>

      {/* Row 5: Downtime Events */}
      {uptime && uptime.events.length > 0 && (
 <Card title="Eventos de Downtime (24h)" icon="" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {uptime.events.map((e, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: theme.input, borderRadius: 8, fontSize: 13,
              }}>
 <span style={{ color: theme.error }}> {fmtDateTime(e.timestamp)}</span>
                <span style={{ color: theme.dimmed }}>{e.duration_seconds}s de downtime</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Row 6: Logs */}
 <Card title={`Logs (última 1h) — ${filteredLogs.length} entradas`} icon="">
        {/* Filter buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {(['all', 'info', 'warn', 'error'] as const).map(level => (
            <Button variant="ghost" key={level} onClick={() => setLogFilter(level)} style={{
              padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
              background: logFilter === level ? theme.accent : theme.input,
              color: logFilter === level ? '#fff' : theme.dimmed,
              transition: 'all 0.2s',
            }}>
 {level === 'all' ? 'Todos' : level === 'info' ? 'Info' : level === 'warn' ? 'Warn' : 'Error'}
            </Button>
          ))}
          <div style={{ flex: 1 }} />
          <Button variant="ghost" onClick={fetchAll} style={{
            padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, background: theme.accent, color: T.text,
          }}>
 Atualizar
          </Button>
        </div>

        {/* Log list */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={28} />)}
          </div>
        ) : filteredLogs.length === 0 ? (
          <div style={{ color: theme.dimmed, textAlign: 'center', padding: 20, fontSize: 14 }}>
            Sem logs no período selecionado
          </div>
        ) : (
          <div style={{
            maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2,
            fontFamily: 'monospace', fontSize: 12,
          }}>
            {filteredLogs.map((log) => (
              <div key={log.id} style={{
                display: 'flex', gap: 8, padding: '6px 10px',
                borderRadius: 4, background: theme.input,
                borderLeft: `3px solid ${log.level === 'error' ? theme.error : log.level === 'warn' ? theme.warning : theme.border}`,
              }}>
                <span style={{ color: theme.dimmed, whiteSpace: 'nowrap' }}>[{fmtTime(log.timestamp)}]</span>
                <span>{levelIcon(log.level)}</span>
                <span style={{ color: theme.text, flex: 1 }}>{log.message}</span>
                <span style={{ color: theme.dimmed, fontSize: 11 }}>{log.source}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: 16, color: theme.dimmed, fontSize: 11 }}>
        Auto-refresh: 3s • Dados: {status?.tunnel_ip || '—'}:{status?.tunnel_port || '—'}
      </div>
    </div>
  );
}
